import { QUALITY_PRESETS, type QualityTier } from '@cinematic-engine/core';
import { drawFrame, loadMedia } from './render-engine';
import type { StudioAsset, StudioTrack } from './types';

export type ExportFormat = 'webm' | 'mp4';

export interface ExportSettings {
  width: number;
  height: number;
  fps: number;
  bitrate: number;
}

export interface ExportRunOptions {
  assets: Map<string, StudioAsset>;
  tracks: StudioTrack[];
  duration: number;
  settings: ExportSettings;
  format: ExportFormat;
  backgroundColor?: string;
  fileName: string;
  onProgress: (progress: number, message: string) => void;
  onResult: (blobUrl: string) => void;
  isCancelled: () => boolean;
}

export function resolveExportSettings(
  tier: QualityTier,
  sourceWidth: number,
  sourceHeight: number,
  sourceFps: number
): ExportSettings {
  const scale = tier === 'DRAFT' ? 0.5 : tier === 'PREVIEW' ? 0.75 : 1;
  return {
    width: Math.max(2, Math.round(sourceWidth * scale) & ~1),
    height: Math.max(2, Math.round(sourceHeight * scale) & ~1),
    fps: Math.min(Math.max(1, Math.round(sourceFps)), 60),
    bitrate: QUALITY_PRESETS[tier].bitrate,
  };
}

export function pickMimeType(format: ExportFormat): string {
  if (format === 'mp4') {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported('video/mp4')) {
      return 'video/mp4';
    }
    throw new Error('MP4 export is not supported by this browser; use WebM instead');
  }
  const candidates = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
  const supported = candidates.find(
    (c) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c)
  );
  if (!supported) {
    throw new Error('MediaRecorder is not supported by this browser');
  }
  return supported;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

interface AudioClipSchedule {
  element: HTMLAudioElement;
  start: number;
  duration: number;
  trimStart: number;
}

function scheduleAudio(
  tracks: StudioTrack[],
  assets: Map<string, StudioAsset>
): AudioClipSchedule[] {
  const schedules: AudioClipSchedule[] = [];
  for (const track of tracks) {
    if (track.kind !== 'audio') continue;
    for (const clip of track.clips) {
      const asset = assets.get(clip.assetId);
      if (!asset?.url) continue;
      const element = document.createElement('audio');
      element.preload = 'auto';
      element.src = asset.url;
      schedules.push({
        element,
        start: clip.start,
        duration: clip.duration,
        trimStart: clip.trimStart,
      });
    }
  }
  return schedules;
}

export async function runExport(options: ExportRunOptions): Promise<boolean> {
  const { settings, format, duration } = options;
  const mimeType = pickMimeType(format);
  const canvas = document.createElement('canvas');
  canvas.width = settings.width;
  canvas.height = settings.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to create export canvas');

  for (const asset of options.assets.values()) {
    await loadMedia(asset);
  }

  const audioContext = new AudioContext();
  const audioDestination = audioContext.createMediaStreamDestination();
  const audioSchedules = scheduleAudio(options.tracks, options.assets);
  for (const schedule of audioSchedules) {
    const source = audioContext.createMediaElementSource(schedule.element);
    source.connect(audioDestination);
  }

  const canvasStream = canvas.captureStream(settings.fps);
  const mixed = new MediaStream([
    ...canvasStream.getVideoTracks(),
    ...audioDestination.stream.getAudioTracks(),
  ]);

  const recorder = new MediaRecorder(mixed, {
    videoBitsPerSecond: settings.bitrate,
    mimeType,
  });
  const chunks: Blob[] = [];
  recorder.ondataavailable = (event) => {
    if (event.data.size > 0) chunks.push(event.data);
  };

  const totalFrames = Math.max(1, Math.round(duration * settings.fps));
  const frameInterval = 1000 / settings.fps;
  const wallStart = performance.now() + 250;
  const audioStart = wallStart;
  let cancelled = false;

  const stopAudio = () => {
    for (const schedule of audioSchedules) {
      schedule.element.pause();
      schedule.element.currentTime = 0;
    }
  };

  try {
    if (audioContext.state === 'suspended') await audioContext.resume();
    recorder.start();

    for (let frame = 0; frame < totalFrames; frame++) {
      if (options.isCancelled()) {
        cancelled = true;
        break;
      }
      const target = wallStart + frame * frameInterval;
      const delay = target - performance.now();
      if (delay > 0) await sleep(delay);

      const t = frame / settings.fps;
      drawFrame(ctx, {
        assets: options.assets,
        tracks: options.tracks,
        time: t,
        width: settings.width,
        height: settings.height,
        backgroundColor: options.backgroundColor,
      });

      const wall = performance.now() - audioStart;
      for (const schedule of audioSchedules) {
        const inWindow =
          wall >= schedule.start * 1000 && wall < (schedule.start + schedule.duration) * 1000;
        if (inWindow && schedule.element.paused) {
          schedule.element.currentTime = wall / 1000 - schedule.start + schedule.trimStart;
          void schedule.element.play().catch(() => undefined);
        }
        if (wall >= (schedule.start + schedule.duration) * 1000 && !schedule.element.paused) {
          schedule.element.pause();
        }
      }

      options.onProgress((frame + 1) / totalFrames, `Frame ${frame + 1}/${totalFrames}`);
    }
  } finally {
    stopAudio();
    void audioContext.close();
  }

  await new Promise<void>((resolve) => {
    if (recorder.state !== 'inactive') {
      recorder.onstop = () => resolve();
      recorder.stop();
    } else {
      resolve();
    }
  });

  if (cancelled) {
    for (const schedule of audioSchedules) schedule.element.src = '';
    return false;
  }

  const type = mimeType.includes(';') ? mimeType.split(';')[0] : mimeType;
  const blob = new Blob(chunks, { type });
  options.onResult(URL.createObjectURL(blob));
  return true;
}
